import type { CarFilters } from '@/scopes/checkout/filterableCars/domain/types'
import type { FilterableCarItem, FilterableCars } from '@/scopes/checkout/filterableCars/domain/types/filterableCars'
import type { FilterMappers } from '@/scopes/checkout/filterableCars/domain/types/filterMappers'
import type { APIFilterableCars } from '@/scopes/checkout/filterableCars/infrastructure/api/types'
import type { APIBookingDraftCarRentalServiceOption } from '@/scopes/checkout/infrastructure/api/types'
import {
  isValidUrl,
  mapAdditionalCosts,
  mapExtraFeatures,
  mapFeatures,
} from '@/scopes/checkout/infrastructure/api/model-mappers/serviceCarRentalOptionMapper'

export interface GetAllCarsResponseMapper {
  map: (data: APIFilterableCars) => FilterableCars
}

interface Dependencies {
  filtersMappers: FilterMappers
}

export function createGetAllCarsResponseMapper(dependencies: Dependencies): GetAllCarsResponseMapper {
  const { filtersMappers: mappers } = dependencies

  function map(data: APIFilterableCars): FilterableCars {
    const { cars, filters } = data

    return {
      filters: {
        ...Object.entries(mappers).reduce((acc, [filterType, filterMapper]) => {
          if (!filters)
            return acc
          const filterData = filters.find(filter => filter.type === filterType)
          if (!filterData)
            return acc
          const mappedFilter = filterMapper.map(filterData)
          if (!mappedFilter)
            return acc

          return {
            ...acc,
            [filterType]: mappedFilter,
          }
        }, {}),
      } as CarFilters,
      cars: cars.reduce<Record<string, FilterableCarItem>>((acc2, car) => {
        const imageUrlLarge = isValidUrl(car.imagePath?.large ?? '') ? car.imagePath?.large : ''
        const imageUrlSmall = isValidUrl(car.imagePath?.small ?? '') ? car.imagePath?.small : ''
        const id = car.uniqueId ?? car.code
        return {
          ...acc2,
          [id]: {
            id,
            value: car.code,
            price: car.price,
            image: imageUrlLarge ?? imageUrlSmall ?? '',
            carClass: mapCarClass(car),
            label: car.carModel ?? '',
            conditionsUrl: car.conditionsUrl ?? '',
            availableOnRequest: car.availableOnRequest ?? false,
            features: mapFeatures(car) ?? [],
            extraFeatures: mapExtraFeatures(car) ?? [],
            additionalCosts: mapAdditionalCosts(car) ?? {},
            canBeRemoved: car.isCancellable ?? false,
            canBeEdited: car.isSelfEditable ?? false,
            canBeUpgraded: car.canBeUpgraded ?? false,
            isIncluded: car.isIncluded ?? false,
          },
        }
      }, {}),
    }
  }
  return { map }
}

function mapCarClass(car: APIBookingDraftCarRentalServiceOption) {
  const carClassFeature = car.features?.find(feature => feature.type === 'carClass')
  if (carClassFeature) {
    return carClassFeature.value.map(text => text).join(', ')
  }
  if (car.carClass) {
    return car.carClass
  }
  return ''
}
